import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, Collapse, Typography } from '@mui/material'
import { useToasts } from 'react-toast-notifications'
import Loader from 'src/designSystem/Loader'
import { checkIfProductionEnvironment } from 'src/lib/checkIfProductionEnvironment'
import { useStalledAfterDays } from '../../api/AdvancedSettingsProvider'
import { useSharedCarePlan } from '../../api/useSharedCarePlan'
import { useAdvancedPatientHistory } from '../../api/useAdvancedPatientHistory'
import { useAdvancedOverviewState } from '../../api/AdvancedOverviewProvider'
import { AdvancedTrackSummary } from '../../api/types'
import { usePanelPatientUid } from '../../dashboard/panelContext'
import { useIsPanelPreview } from '../../dashboard/panelPreviewContext'
import { trackTitle } from '../../dashboard/tracks/trackTransitions'
import { useTrackActions } from '../../dashboard/tracks/useTrackActions'
import EmptyState from '../../components/EmptyState'
import { ProviderRoleId } from '../../currentRole'
import RichTextEditor from './RichTextEditor'
import CarePlanGrid from './CarePlanGrid'
import CarePlanHeader from './CarePlanHeader'
import PlanTimeSimulator from './PlanTimeSimulator'
import WeeklyContentPlanner from './WeeklyContentPlanner'
import { carePlanActions } from './carePlanActions'
import {
  CarePlanState,
  carePlanStateKey,
  ensurePsychiatricSupervision,
  carePlanTitle,
  emptyState,
  isPsychiatristPlanHidden,
} from './carePlanState'
import { trackRowsFor } from './categories'
import { nowColumn, planMonthOf, simulatedPlanTime, unplannedItemsFor } from './livePlan'
import { planTrackDecorations } from './planTrackDecorations'

const NO_TRACKS: AdvancedTrackSummary[] = []

type Props = {
  role: ProviderRoleId
  readOnly: boolean
}

const CarePlanBody = ({ role, readOnly }: Props) => {
  const uid = usePanelPatientUid()
  const preview = useIsPanelPreview()
  const stateKey = carePlanStateKey(role, uid)
  // Preview is look-only like readOnly, but not isPsychiatristPlanHidden (ADR-049).
  const viewOnly = readOnly || preview
  const plan = useSharedCarePlan<CarePlanState>(uid, role, emptyState(), { writable: !viewOnly })
  const { value: state, set: setState } = plan
  const [isEditing, setIsEditing] = useState(false)
  const { addToast } = useToasts()
  const { conflicted, dismissConflict } = plan

  useEffect(() => {
    if (conflicted) {
      addToast('Someone else on the care team saved this plan first. You are now seeing their version.', {
        appearance: 'warning',
      })
      dismissConflict()
    }
  }, [conflicted, dismissConflict, addToast])
  const [notesOpen, setNotesOpen] = useState(false)
  const [simulatedWeek, setSimulatedWeek] = useState<number | null>(null)

  // Not `useTracks()`: that throws outside the Progress tab, and this panel lives on window tabs.
  const overview = useAdvancedOverviewState()
  const summary = overview.status === 'ready' ? overview.overview.summary : null
  const tracks = summary?.tracks ?? NO_TRACKS
  const trackActions = useTrackActions(uid)
  const { catalog } = trackActions
  const history = useAdvancedPatientHistory(uid)
  const gridTrackRows = trackRowsFor(
    catalog,
    tracks.map((track) => track.trackKey)
  )
  const actions = carePlanActions(state, setState, gridTrackRows)

  const editable = !viewOnly && isEditing
  // Starting or ending a track is a care action, not a plan edit, so it doesn't wait for Edit.
  const canActOnTracks = !viewOnly && summary !== null && !trackActions.busy
  const anchor = summary?.firstAcceptedStartAt ?? null
  // The test clock only moves what the plan shows; accepting a track still counts from the real `anchor`.
  const planTime =
    !checkIfProductionEnvironment() && simulatedWeek !== null
      ? simulatedPlanTime(simulatedWeek, anchor, new Date())
      : { anchor, now: new Date() }
  const { now } = planTime
  const currentMonth = summary ? planMonthOf(planTime.anchor, now) : null
  const stalledAfterDays = useStalledAfterDays()
  const { decorationFor, confirmMove } = planTrackDecorations({
    stalledAfterDays,
    catalog,
    tracks,
    history,
    trackRows: gridTrackRows,
    trackActions,
    actions,
    viewOnly,
    canActOnTracks,
    hasLiveState: summary !== null,
    anchor,
    planTime,
    currentMonth,
  })
  const usesWeeklyContentPlanner = role === 'therapist' || role === 'nutritionist'
  const status = state.status ?? 'draft'

  // Preview writes nothing (ADR-049): a gallery thumbnail would rewrite the real record.
  useEffect(() => {
    if (preview || !plan.exists || role !== 'psychiatrist') {
      return
    }
    setState(ensurePsychiatricSupervision)
  }, [stateKey, plan.exists, role, preview, setState])

  const handleSave = (next: 'draft' | 'submitted') => () => {
    setState((prev) => ({ ...prev, status: next, reviewPending: false }))
    setIsEditing(false)
  }

  if (plan.status === 'loading') {
    return <Loader size="small" />
  }
  if (plan.status === 'error') {
    return (
      <EmptyState
        title="Failed to load this care plan"
        action={
          <Button sx={{ mt: 1 }} onClick={plan.retry}>
            Try again
          </Button>
        }
      />
    )
  }
  if (!plan.exists) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="overline">{carePlanTitle(role)}</Typography>
        <EmptyState
          title="No care plan yet"
          description={
            role === 'psychiatrist' ? "It's created when the psychiatrist submits the intake note." : undefined
          }
          action={
            !viewOnly && (
              <Button sx={{ mt: 1 }} onClick={() => setState(emptyState())}>
                Start a blank plan
              </Button>
            )
          }
        />
      </Box>
    )
  }
  if (isPsychiatristPlanHidden(readOnly, role, status)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="overline">{carePlanTitle(role)}</Typography>
        <EmptyState title="Not yet published" description="The psychiatrist hasn't saved & submitted this plan yet." />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CarePlanHeader
        role={role}
        status={status}
        viewOnly={viewOnly}
        editable={editable}
        onEdit={() => setIsEditing(true)}
        onRefresh={() =>
          addToast('Nothing to refresh yet — this will sync connected data sources in a future update.', {
            appearance: 'info',
            autoDismissTimeout: 4000,
          })
        }
      />

      {(overview.status === 'error' || overview.status === 'unavailable') && (
        <Alert
          severity="warning"
          action={
            overview.status === 'error' && (
              <Button color="inherit" size="small" onClick={overview.retry}>
                Retry
              </Button>
            )
          }
        >
          Live track status couldn&apos;t load, so tracks can&apos;t be started, accepted or ended here, and bars
          don&apos;t show their state. {overview.status === 'unavailable' && 'Reload the page to try again.'}
        </Alert>
      )}

      {!viewOnly && state.reviewPending && (
        <Alert severity="info">
          Created from the intake note. Review it, assign leads where you know them, then save.
        </Alert>
      )}

      {readOnly ? (
        <Box>
          <Button size="small" aria-expanded={notesOpen} onClick={() => setNotesOpen((open) => !open)}>
            {notesOpen ? 'Hide notes' : 'Show notes'}
          </Button>
          <Collapse in={notesOpen}>
            <RichTextEditor
              key={stateKey}
              value={state.notesHtml ?? ''}
              onChange={actions.setNotesHtml}
              editable={false}
            />
          </Collapse>
        </Box>
      ) : (
        <RichTextEditor
          key={stateKey}
          value={state.notesHtml ?? ''}
          onChange={actions.setNotesHtml}
          editable={editable}
        />
      )}

      {usesWeeklyContentPlanner ? (
        <WeeklyContentPlanner
          role={role}
          data={state.weeklyPlanner ?? emptyState().weeklyPlanner}
          onChange={actions.setWeeklyPlanner}
          editable={editable}
        />
      ) : (
        <>
          <CarePlanGrid
            state={state}
            actions={actions}
            editable={editable}
            trackRows={gridTrackRows}
            tracksEditable={!viewOnly}
            titleFor={(trackKey) => trackTitle(catalog, trackKey)}
            decorationFor={decorationFor}
            unplannedItemsFor={
              summary ? (row, planned) => unplannedItemsFor(row, planned, tracks, planTime.anchor, now) : undefined
            }
            currentMonth={currentMonth}
            nowColumn={summary ? nowColumn(planTime.anchor, now) : null}
            onMove={confirmMove}
            toolbar={
              !checkIfProductionEnvironment() && summary ? (
                <PlanTimeSimulator week={simulatedWeek} onChange={setSimulatedWeek} />
              ) : undefined
            }
          />
          {trackActions.dialog}
        </>
      )}

      {role === 'psychiatrist' && !state.itemsByRowKey?.['modules.nutrition']?.some((item) => item.trackKey) && (
        <Alert severity="warning">
          No nutrition track has been added. Click on the plus sign in the Nutrition row in the right month (likely
          month 2) to select between optimal nutrition and ketogenic diet when ready.
        </Alert>
      )}

      {!viewOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button variant="outlined" disabled={!editable} onClick={handleSave('draft')}>
            Save as Draft
          </Button>
          <Button variant="contained" disabled={!editable} onClick={handleSave('submitted')}>
            Save & Submit
          </Button>
        </Box>
      )}

      {plan.saveFailed && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={plan.retrySave}>
              Retry
            </Button>
          }
        >
          Failed to save this plan.
        </Alert>
      )}
    </Box>
  )
}

export default CarePlanBody
